import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import GridRowContextMenu, {
    buildInventoryLevelsShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
    useSyncedUrlSearchQuery,
} from "../Helpers/GridRowContextMenu.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { INVENTORY_LEVELS_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import {
    catalogDataGridSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";
import "./catalog.css";

const DEFAULT_SORT = [{ field: "totalQuantOnHand", sort: "desc" }];

function QtyCell({ value }) {
    if (value == null || value === "") {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    return (
        <Typography
            component="span"
            sx={{
                fontSize: 13.5,
                fontWeight: n === 0 ? 500 : 700,
                color: n === 0 ? "#9aa3ad" : "#1c2430",
            }}
        >
            {n.toLocaleString("en-US")}
        </Typography>
    );
}

function NameCell({ value }) {
    const text = value || "";
    if (!text) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    return (
        <Tooltip title={text} enterDelay={300} placement="top-start">
            <Box
                component="span"
                sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                    display: "block",
                    fontWeight: 600,
                }}
            >
                {text}
            </Box>
        </Tooltip>
    );
}

export default function InventoryLevelsGrid() {
    const { searchInput, setSearchInput, searchQ } = useSyncedUrlSearchQuery();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQ]);

    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (row) => buildInventoryLevelsShareUrl(row),
                disabled: (row) =>
                    !String(
                        row?.internalSkuId || row?.listingField || row?.itemName || ""
                    ).trim(),
                onCopied: () =>
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    }),
            }),
        ],
        []
    );

    const {
        filterActive,
        activeConditions,
        conjunction,
        barProps: filterBar,
    } = useGridFilter(INVENTORY_LEVELS_FILTER_FIELDS);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.inventoryLevels, {
        sortModel: DEFAULT_SORT,
        columnVisibilityModel: {},
    });

    const sortForApi = useMemo(() => {
        const entry = layout.sortModel?.[0];
        if (entry?.field) {
            return {
                sort: entry.field,
                sortDir: entry.sort === "asc" ? "asc" : "desc",
            };
        }
        return { sort: "totalQuantOnHand", sortDir: "desc" };
    }, [layout.sortModel]);

    const filtersParam = useMemo(() => {
        if (!activeConditions.length) return "";
        return JSON.stringify({
            conjunction,
            conditions: activeConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [activeConditions, conjunction]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, conjunction]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(paginationModel.page + 1),
            pageSize: String(paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        return `api/broylesllc/products?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filtersParam,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, error, load } = useApiData(listUrl);

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : 0);
        setRows(
            items.map((p) => ({
                id: p._id,
                itemName: p.itemName || p.listingField || "",
                listingField: p.listingField || "",
                internalSkuId: p.internalSkuId || "",
                totalQuantOnHand: p.totalQuantOnHand,
                availableQuantityWalmart: p.availableQuantityWalmart,
                availableQuantityEbay: p.availableQuantityEbay,
                availableQuantityShopify: p.availableQuantityShopify,
            }))
        );
    }, [data]);

    const totals = useMemo(() => {
        return rows.reduce(
            (acc, row) => {
                acc.total += Number(row.totalQuantOnHand) || 0;
                acc.walmart += Number(row.availableQuantityWalmart) || 0;
                acc.ebay += Number(row.availableQuantityEbay) || 0;
                acc.shopify += Number(row.availableQuantityShopify) || 0;
                return acc;
            },
            { total: 0, walmart: 0, ebay: 0, shopify: 0 }
        );
    }, [rows]);

    const baseColumns = useMemo(
        () => [
            {
                field: "itemName",
                headerName: "Product Name",
                minWidth: 240,
                flex: 1.4,
                renderCell: (params) => <NameCell value={params.value} />,
            },
            {
                field: "listingField",
                headerName: "Listing Field",
                minWidth: 180,
                flex: 1,
                renderCell: (params) => <NameCell value={params.value} />,
            },
            {
                field: "internalSkuId",
                headerName: "Internal SKU",
                minWidth: 150,
                flex: 0.8,
                renderCell: (params) => <NameCell value={params.value} />,
            },
            {
                field: "totalQuantOnHand",
                headerName: "Total",
                minWidth: 110,
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: (params) => <QtyCell value={params.value} />,
            },
            {
                field: "availableQuantityWalmart",
                headerName: "Walmart",
                minWidth: 110,
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: (params) => <QtyCell value={params.value} />,
            },
            {
                field: "availableQuantityEbay",
                headerName: "eBay",
                minWidth: 110,
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: (params) => <QtyCell value={params.value} />,
            },
            {
                field: "availableQuantityShopify",
                headerName: "Shopify",
                minWidth: 110,
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: (params) => <QtyCell value={params.value} />,
            },
        ],
        []
    );

    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    const syncListingQuantities = async () => {
        setSyncing(true);
        const result = await PostData({}, "broylesllc/products/sync-listing-quantities");
        setSyncing(false);
        if (result?.success) {
            setSnack({
                open: true,
                color: "success",
                message:
                    result?.data?.message ||
                    "Listing quantity sync started for Walmart, eBay, and Shopify.",
            });
            return;
        }
        setSnack({
            open: true,
            color: "error",
            message:
                result?.error?.response?.data?.message ||
                "Could not start listing quantity sync.",
        });
    };

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    Failed to load inventory levels.
                </Typography>
            )}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search product name or SKU…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <span className="catalog-selection-hint">
                        {rowCount} product{rowCount === 1 ? "" : "s"}
                        {filterActive ? " matching filters" : ""}
                    </span>
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <span className="catalog-selection-hint">
                        Page · Total {totals.total.toLocaleString("en-US")} · Walmart{" "}
                        {totals.walmart.toLocaleString("en-US")} · eBay{" "}
                        {totals.ebay.toLocaleString("en-US")} · Shopify{" "}
                        {totals.shopify.toLocaleString("en-US")}
                    </span>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={syncing}
                        startIcon={
                            syncing ? (
                                <CircularProgress size={14} />
                            ) : (
                                <SyncOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={syncListingQuantities}
                        sx={catalogQuietButtonSx}
                    >
                        Sync to platforms
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load)}
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        sortingMode="server"
                        sortModel={layout.sortModel?.length ? layout.sortModel : DEFAULT_SORT}
                        onSortModelChange={(model) => {
                            layout.onSortModelChange(model?.length ? model : DEFAULT_SORT);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={gridSlotProps}
                        sx={catalogDataGridSx}
                        localeText={{
                            noRowsLabel: searchQ || filterActive
                                ? "No products match that search."
                                : "No products yet.",
                        }}
                    />
                </div>
            </div>
            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />
            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </>
    );
}
